import React, { useMemo } from "react";
import Highcharts from "../../lib/highchartsExtended";
import type { Options as HCOptions } from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../providers/ThemeProvider";
import type { MachineSummaryRow } from "../../services/ShiftAnalysisService";
import { msToSeconds } from "./utils";
import Panel from "./Panel";

interface Props {
  machines: MachineSummaryRow[];
  focusedSn: string | null;
  onHover: (sn: string | null) => void;
  onPin: (sn: string) => void;
}

// Distinct, color-blind-friendly palette for machine series.
const PALETTE = [
  "#3b82f6",
  "#ef4444",
  "#10b981",
  "#f59e0b",
  "#a855f7",
  "#06b6d4",
  "#ec4899",
  "#84cc16",
  "#f97316",
  "#14b8a6",
  "#6366f1",
  "#eab308",
];

const NOMINAL_SHIFT_SEC = 8 * 3600;
// Reject rate (0–1) at which Quality hits 0. 3% reject is already very bad,
// so the Quality axis is scaled against this threshold rather than (1−reject).
const REJECT_ZERO_QUALITY = 0.03;

const clamp = (n: number) => Math.max(0, Math.min(100, n));

const MachinePolar: React.FC<Props> = ({
  machines,
  focusedSn,
  onHover,
  onPin,
}) => {
  const { t } = useTranslation();
  const { currentTheme } = useTheme();
  const isDark = currentTheme.isDark;

  const textColor = isDark ? "#cbd5e1" : "#475569";
  const gridColor = isDark ? "rgba(148,163,184,0.22)" : "rgba(15,23,42,0.12)";

  const categories = useMemo(
    () => [
      t("efficiency", "Efficiency"),
      t("utilization", "Utilization"),
      t("uptime", "Uptime"),
      t("quality", "Quality"),
      t("output", "Output"),
    ],
    [t],
  );

  const options = useMemo<HCOptions>(() => {
    const maxOutput = machines.reduce((m, x) => Math.max(m, x.output), 0) || 1;

    const series = machines.map((m, i) => {
      const color = PALETTE[i % PALETTE.length];
      const isFocused = focusedSn === m.machine_sn;
      const dimmed = focusedSn !== null && !isFocused;

      const efficiency = clamp(m.efficiency * 100);
      const utilization = clamp(m.utilization * 100);
      const uptime = clamp(
        100 - (msToSeconds(m.downtime_ms) / NOMINAL_SHIFT_SEC) * 100,
      );
      const quality = clamp((1 - m.reject_rate / REJECT_ZERO_QUALITY) * 100);
      const output = clamp((m.output / maxOutput) * 100);

      return {
        type: "area" as const,
        name: m.machine_name,
        color,
        data: [efficiency, utilization, uptime, quality, output],
        fillOpacity: dimmed ? 0.02 : 0.16,
        lineWidth: isFocused ? 3.5 : 2,
        opacity: dimmed ? 0.28 : 1,
        zIndex: isFocused ? 5 : 1,
        marker: {
          enabled: false,
          radius: 3,
          states: { hover: { enabled: true } },
        },
        custom: { sn: m.machine_sn },
        events: {
          mouseOver() {
            const sn = (
              this as unknown as { options?: { custom?: { sn?: string } } }
            ).options?.custom?.sn;
            if (sn) onHover(sn);
          },
          mouseOut() {
            onHover(null);
          },
          click() {
            const sn = (
              this as unknown as { options?: { custom?: { sn?: string } } }
            ).options?.custom?.sn;
            if (sn) onPin(sn);
          },
          legendItemClick() {
            const sn = (
              this as unknown as { options?: { custom?: { sn?: string } } }
            ).options?.custom?.sn;
            if (sn) onPin(sn);
            return false; // don't toggle visibility; use click to focus instead
          },
        },
      };
    });

    return {
      chart: {
        polar: true,
        type: "area",
        backgroundColor: "transparent",
        height: 360,
        spacing: [6, 6, 6, 6],
        animation: { duration: 900 },
      },
      title: undefined,
      credits: { enabled: false },
      pane: { size: "78%" },
      xAxis: {
        categories,
        tickmarkPlacement: "on",
        lineWidth: 0,
        gridLineColor: gridColor,
        gridLineWidth: 1,
        labels: {
          style: { color: textColor, fontSize: "11px", fontWeight: "600" },
        },
      },
      yAxis: {
        min: 0,
        max: 100,
        tickInterval: 20,
        gridLineInterpolation: "polygon",
        gridLineColor: gridColor,
        lineWidth: 0,
        labels: {
          style: { color: textColor, fontSize: "9px" },
          y: 2,
        },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: isDark ? "#1e293b" : "#ffffff",
        borderColor: gridColor,
        borderRadius: 10,
        shadow: true,
        style: { color: isDark ? "#f1f5f9" : "#0f172a", fontSize: "11px" },
        valueDecimals: 0,
        valueSuffix: "%",
      },
      legend: {
        enabled: true,
        align: "center",
        verticalAlign: "bottom",
        itemStyle: { color: textColor, fontSize: "11px", fontWeight: "500" },
        itemHoverStyle: { color: isDark ? "#fff" : "#000" },
        maxHeight: 64,
      },
      plotOptions: {
        series: {
          cursor: "pointer",
          states: { inactive: { opacity: 0.2 } },
        },
      },
      series,
    };
  }, [
    machines,
    focusedSn,
    isDark,
    gridColor,
    textColor,
    categories,
    onHover,
    onPin,
  ]);

  return (
    <Panel
      label={t("machineProfile", "Machine profile")}
      meta={t("kpiComparison", "KPI comparison across machines")}
    >
      {machines.length === 0 ? (
        <div
          className="py-10 text-center text-sm"
          style={{ color: "var(--text-secondary)" }}
        >
          {t("noMachineSummary", "No machine data available for this shift.")}
        </div>
      ) : (
        <HighchartsReact highcharts={Highcharts} options={options} />
      )}
    </Panel>
  );
};

export default MachinePolar;
